import { Rocket, Shield, BarChart, Layers, GamepadIcon, Heart, Image, PlusCircle, MinusCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { languageService } from '../../js/languageService';

export default function RoadmapSection() {
  const [activeItem, setActiveItem] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(prev => prev + 1);
      setActiveItem(null);
      setActiveFaq(null);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);
  
  const roadmapItems = [
    {
      title: 'roadmap.presale',
      icon: <Rocket size={24} />,
      status: 'roadmap.status_in_progress'
    },
    {
      title: 'roadmap.certik_audit',
      icon: <Shield size={24} />,
      status: 'roadmap.status_coming_soon'
    },
    {
      title: 'roadmap.dex_listing',
      icon: <BarChart size={24} />,
      status: 'roadmap.status_planned'
    },
    {
      title: 'roadmap.staking_launch',
      icon: <Layers size={24} />,
      status: 'roadmap.status_future'
    },
    {
      title: 'roadmap.interactive_nfts',
      icon: <Image size={24} />,
      status: 'roadmap.status_future'
    },
    {
      title: 'roadmap.ai_minigame',
      icon: <GamepadIcon size={24} />,
      status: 'roadmap.status_vision'
    },
    {
      title: 'roadmap.charity_foundation',
      icon: <Heart size={24} />,
      status: 'roadmap.status_vision'
    },
  ];
  
  const faqItems = [
    {
      question: 'faq.presale_start',
      answer: 'faq.presale_start_answer'
    },
    {
      question: 'faq.security_funds',
      answer: 'faq.security_funds_answer'
    },
    {
      question: 'faq.token_trading',
      answer: 'faq.token_trading_answer'
    },
    {
      question: 'faq.token_utility',
      answer: 'faq.token_utility_answer'
    },
    {
      question: 'faq.staking_program',
      answer: 'faq.staking_program_answer'
    }
  ];
  
  // Function to get the status color
  const getStatusColor = (status) => {
    const statusText = languageService.getTranslation(status);
    switch(statusText) {
      case languageService.getTranslation('roadmap.status_in_progress'): return 'from-green-400 to-emerald-600';
      case languageService.getTranslation('roadmap.status_coming_soon'): return 'from-blue-400 to-indigo-600';
      case languageService.getTranslation('roadmap.status_planned'): return 'from-purple-400 to-pink-600';
      case languageService.getTranslation('roadmap.status_future'): return 'from-amber-400 to-orange-600';
      case languageService.getTranslation('roadmap.status_vision'): return 'from-rose-400 to-red-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };
  
  return (
    <section id="roadmap" className="py-8 relative" key={forceUpdate}>
      {/* Decorative elements - matching other sections */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-blue-950/2 to-transparent pointer-events-none"></div>
      <div className="absolute -bottom-20 right-0 w-80 h-80 bg-purple-900/5 rounded-full blur-[100px]"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left column - Vertical roadmap */}
          <div className="lg:col-span-1">
            <div className="glass-card p-8 hover-glow border border-blue-500/10 h-full">
              <h3 className="text-2xl font-bold mb-4 gradient-heading inline-block pb-1 border-b-2 border-primary/30" data-i18n="roadmap.title">{languageService.getTranslation('roadmap.title')}</h3>
              
              <div className="relative">
                <div className="absolute top-0 bottom-0 w-0.5 left-5 bg-gradient-to-b from-blue-600/50 via-purple-600/50 to-blue-600/30 z-0"></div>
                
                <div className="space-y-3">
                  {roadmapItems.map((item, index) => (
                    <div 
                      key={index}
                      className="relative pl-20"
                      onMouseEnter={() => setActiveItem(index)}
                      onMouseLeave={() => setActiveItem(null)}
                    >
                      <div className={`absolute left-5 top-7 -translate-x-1/2 w-6 h-6 rounded-full z-10 bg-gradient-to-r ${getStatusColor(item.status)} flex items-center justify-center text-xs font-bold text-white shadow-[0_0_10px_rgba(59,130,246,0.5)]`}>
                        {index + 1}
                      </div>
                      
                      <div 
                        className={`
                          glass-card backdrop-blur-sm rounded-xl p-4 
                          border border-gray-700/20 hover:border-primary/50
                          transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,130,246,0.2)]
                          ${activeItem === index ? 'shadow-md scale-[1.02]' : ''}
                        `}
                      >
                        <div className="flex items-center space-x-4">
                          <div className={`bg-gradient-to-br ${getStatusColor(item.status)} p-2.5 rounded-lg shadow-md text-white flex-shrink-0`}>
                            {item.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold card-subtitle" data-i18n={item.title}>{languageService.getTranslation(item.title)}</h3>
                          </div>
                          <div className={`hidden lg:block ml-2 text-xs font-medium py-1 px-2 rounded-md bg-gradient-to-r ${getStatusColor(item.status)} text-white opacity-90 whitespace-nowrap`} data-i18n={item.status}>
                            {languageService.getTranslation(item.status)}
                          </div>
                        </div>

                        <div className={`absolute left-5 top-7 w-[3.75rem] h-px bg-gradient-to-r ${getStatusColor(item.status)}`}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Right column - FAQ */}
          <div className="lg:col-span-1">
            <div className="glass-card p-8 hover-glow border border-blue-500/10 h-full">
              <h3 className="text-2xl font-bold mb-4 gradient-heading inline-block pb-1 border-b-2 border-primary/30" data-i18n="faq.title">{languageService.getTranslation('faq.title')}</h3>
              
              <div className="space-y-2">
                {faqItems.map((faq, index) => (
                  <div 
                    key={index}
                    className={`glass-card rounded-xl backdrop-blur-sm border border-gray-700/20 hover:border-primary/30 transition-colors overflow-hidden hover:shadow-[0_0_15px_rgba(59,130,246,0.2)]`}
                  >
                    <button 
                      className="w-full p-5 flex items-center justify-between text-left"
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    >
                      <h4 className="text-lg font-medium card-subtitle" data-i18n={faq.question}>{languageService.getTranslation(faq.question)}</h4>
                      <div className="text-primary ml-3 flex-shrink-0">
                        {activeFaq === index ? 
                          <MinusCircle size={20} /> : 
                          <PlusCircle size={20} />
                        }
                      </div>
                    </button>
                    
                    <div 
                      className={`px-5 transition-all duration-300 ${
                        activeFaq === index ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 overflow-hidden opacity-0'
                      }`}
                    >
                      <div className="border-t border-gray-700/20 pt-4">
                        <p className="card-text text-sm leading-relaxed whitespace-pre-line" data-i18n={faq.answer}>{languageService.getTranslation(faq.answer)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 